import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { toast } from 'sonner';
import ProtectedLayout from 'hoc/ProtectedLayout';
import { useMe } from 'hooks/auth';
import { useUpdateMe, type UpdateMeParams } from 'hooks/user';
import { Switch } from 'components';
import { trackError } from 'utils/trackError';
import { getApiErrorMessage, parseApiError } from 'utils/apiError';
import { LanguageSettingsCard } from './LanguageSettingsCard';
import {
  SettingDescription,
  SettingLabel,
  SettingRow,
  SettingsCard,
  SettingsCardContent,
  SettingsCardHeader,
  SettingsCardTitle,
  SettingText,
  Wrapper,
} from './settings.styles';

export default function SettingsScreen() {
  const { t } = useTranslation();
  const { data: user } = useMe();
  const { mutateAsync: updateMe, isPending: isSaving } = useUpdateMe();

  const startWithoutRecording = user?.startWithoutRecordingByDefault ?? false;

  const saveSetting = async (changes: UpdateMeParams, errorMessage?: string) => {
    try {
      await updateMe(changes);
      toast.success(t('profileUpdated'));
    } catch (error) {
      const { status, code } = parseApiError(error);
      trackError(error, {
        context: 'updateProfile',
        isAxiosError: isAxiosError(error),
        status,
        code,
      });
      toast.error(errorMessage ?? getApiErrorMessage(error, t));
    }
  };

  return (
    <ProtectedLayout title={t('settingsScreen.title')} hasBackButton={true}>
      <Wrapper>
        <SettingsCard>
          <SettingsCardHeader>
            <SettingsCardTitle>{t('settingsScreen.conversationsSection')}</SettingsCardTitle>
          </SettingsCardHeader>
          <SettingsCardContent>
            <SettingRow>
              <SettingText>
                <SettingLabel htmlFor="start-without-recording" $active={startWithoutRecording}>
                  {t('settingsScreen.startWithoutRecordingTitle')}
                </SettingLabel>
                <SettingDescription id="start-without-recording-description">
                  {t('settingsScreen.startWithoutRecordingDescription')}
                </SettingDescription>
              </SettingText>
              <Switch
                id="start-without-recording"
                aria-describedby="start-without-recording-description"
                checked={startWithoutRecording}
                disabled={isSaving}
                onCheckedChange={(checked: boolean) => {
                  void saveSetting({ startWithoutRecordingByDefault: checked });
                }}
              />
            </SettingRow>
          </SettingsCardContent>
        </SettingsCard>
        <LanguageSettingsCard
          enabledLanguages={user?.additionalLanguages ?? []}
          disabled={isSaving}
          onChange={(additionalLanguages) => {
            void saveSetting({ additionalLanguages }, t('settingsScreen.languagesSaveError'));
          }}
        />
      </Wrapper>
    </ProtectedLayout>
  );
}
